import { createSelector } from "reselect";
import { RootState } from "@store/index";
import { WebRTCConnectionState } from "./connectionSlice";
import { selectIsDpvrDevice } from "@utils/redux-utils/selectors/productSelectors";

// Base selector for the connection slice
export const selectConnectionState = (state: RootState) => state.connection;

// WebSocket selectors
export const selectWebSocketState = createSelector(
  [selectConnectionState],
  (connection) => connection?.websocket?.state,
);

export const selectIsWebSocketOpen = createSelector(
  [selectConnectionState],
  (connection) => connection?.websocket?.isOpen || false,
);

export const selectWebSocketReconnectAttempts = createSelector(
  [selectConnectionState],
  (connection) => connection?.websocket?.reconnectAttempts || 0,
);

export const selectWebSocketRttMs = createSelector(
  [selectConnectionState],
  (connection) => connection?.websocket?.lastRttMs ?? null,
);

export const selectWebSocketRttUpdatedAt = createSelector(
  [selectConnectionState],
  (connection) => connection?.websocket?.rttUpdatedAt ?? null,
);

export const selectStartDelivery = createSelector(
  [selectConnectionState],
  (connection) => connection?.startDelivery ?? null,
);

export const selectSystemOverlay = createSelector(
  [selectConnectionState],
  (connection) => connection?.systemOverlay ?? null,
);

export const selectIsSystemOverlayOpen = createSelector(
  [selectSystemOverlay],
  (systemOverlay) => systemOverlay?.open ?? false,
);

export const selectIsSystemOverlayWarningDismissed = createSelector(
  [selectSystemOverlay],
  (systemOverlay) => systemOverlay?.dismissed ?? false,
);

export const selectShouldShowSystemOverlayWarning = createSelector(
  [selectIsSystemOverlayOpen, selectIsSystemOverlayWarningDismissed, selectIsDpvrDevice],
  (isOpen, isDismissed, isDpvrDevice) => isOpen && !isDismissed && !isDpvrDevice,
);

export const selectEyeTrackingFailure = createSelector(
  [selectConnectionState],
  (connection) => connection?.eyeTrackingFailure ?? null,
);

// WebRTC selectors
export const selectWebRTCState = createSelector(
  [selectConnectionState],
  (connection) => connection?.webrtc?.state,
);

export const selectIsWebRTCConnected = createSelector(
  [selectWebRTCState],
  (state) => state === WebRTCConnectionState.CONNECTED,
);

export const selectIsWebRTCConnecting = createSelector(
  [selectWebRTCState],
  (state) => state === WebRTCConnectionState.CONNECTING,
);

export const selectIsWebRTCDisconnected = createSelector(
  [selectWebRTCState],
  (state) => state === WebRTCConnectionState.DISCONNECTED || state === WebRTCConnectionState.FAILED,
);

export const selectIsWebRTCDataChannelOpen = createSelector(
  [selectConnectionState],
  (connection) => connection?.webrtc?.isDataChannelOpen || false,
);

export const selectWebRTCError = createSelector(
  [selectConnectionState],
  (connection) => connection?.webrtc?.lastError,
);
